import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const EMPTY_SITE = { nome: "", tipologia: "Sede operativa", indirizzo: "", citta: "", provincia: "", cap: "", nazione: "Italia", note: "" };

export default function SitesSection({ companyId, canEdit }) {
  const [sites, setSites] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [siteForm, setSiteForm] = useState(EMPTY_SITE);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/sites`);
      setSites(data);
    } catch (err) {
      toast.error("Errore caricamento sedi", { description: formatApiError(err) });
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const addSite = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${companyId}/sites`, siteForm);
      toast.success("Sede aggiunta", { description: siteForm.nome });
      setDialogOpen(false);
      setSiteForm(EMPTY_SITE);
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const removeSite = async (site) => {
    try {
      await api.delete(`/companies/${companyId}/sites/${site.id}`);
      toast.success("Sede eliminata", { description: site.nome });
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  return (
    <>
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4 text-green-700" aria-hidden="true" /> Sedi operative e unità locali
          </CardTitle>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)} data-testid="add-site-button">
              <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Aggiungi sede
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {sites.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="sites-empty">Nessuna sede operativa registrata.</p>
          ) : (
            <Table data-testid="sites-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipologia</TableHead>
                  <TableHead>Indirizzo</TableHead>
                  <TableHead>Città</TableHead>
                  {canEdit && <TableHead className="text-right">Azioni</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sites.map((s) => (
                  <TableRow key={s.id} data-testid={`site-row-${s.nome.slice(0, 10)}`}>
                    <TableCell className="font-medium">{s.nome}</TableCell>
                    <TableCell>{s.tipologia}</TableCell>
                    <TableCell>{s.indirizzo}</TableCell>
                    <TableCell>{s.citta} {s.provincia && `(${s.provincia})`}</TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-red-700" onClick={() => removeSite(s)} data-testid={`delete-site-${s.nome.slice(0, 10)}`}>
                          <Trash2 className="w-4 h-4 mr-1" aria-hidden="true" /> Elimina
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="site-dialog">
          <DialogHeader>
            <DialogTitle>Nuova sede operativa</DialogTitle>
            <DialogDescription>Registra una sede operativa o un'unità locale dell'azienda.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addSite} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="site-nome">Nome sede *</Label>
              <Input id="site-nome" required minLength={2} value={siteForm.nome}
                onChange={(e) => setSiteForm({ ...siteForm, nome: e.target.value })} data-testid="site-nome-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="site-indirizzo">Indirizzo</Label>
                <Input id="site-indirizzo" value={siteForm.indirizzo}
                  onChange={(e) => setSiteForm({ ...siteForm, indirizzo: e.target.value })} data-testid="site-indirizzo-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-citta">Città</Label>
                <Input id="site-citta" value={siteForm.citta}
                  onChange={(e) => setSiteForm({ ...siteForm, citta: e.target.value })} data-testid="site-citta-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="site-cancel">Annulla</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="site-submit">Aggiungi</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
